import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '../Components/ui/card';
import { Button } from '../Components/ui/button';
import { Badge } from '../Components/ui/badge';
import { Input } from '../Components/ui/input';
import { Label } from '../Components/ui/label';
import { Select, SelectItem, SelectTrigger, SelectValue, SelectContent } from '../Components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../Components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../Components/ui/dialog';
import { Loader2, Package, Plus, Trash2, ArrowRight, UserCog, History, RefreshCw, Box, Truck, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { format } from 'date-fns';

export default function CreateBatch() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { addToast } = useToast();
    const [selectedItems, setSelectedItems] = useState([]);
    const [assignedStaff, setAssignedStaff] = useState('');
    const [notes, setNotes] = useState('');
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

    // Fetch available items
    const { data: availableItems, isLoading: loadingItems } = useQuery({
        queryKey: ['availableItems'],
        queryFn: async () => {
            const { data } = await apiClient.get('/admin/available-items');
            return data;
        },
    });

    // Fetch batch staff
    const { data: staffList } = useQuery({
        queryKey: ['batchStaff'],
        queryFn: async () => {
            const { data } = await apiClient.get('/admin/batch-staff');
            return data;
        },
    });

    const createBatchMutation = useMutation({
        mutationFn: async (batchData) => {
            const { data } = await apiClient.post('/admin/create-batch', batchData);
            return data;
        },
        onSuccess: () => {
            addToast('Batch created successfully!', 'success');
            queryClient.invalidateQueries(['availableItems']);
            queryClient.invalidateQueries(['adminAllBatches']);
            setSelectedItems([]);
            setAssignedStaff('');
            setNotes('');
            setIsMobileDrawerOpen(false);
            navigate('/list-batches');
        },
        onError: (err) => {
            addToast(err.response?.data?.msg || 'Failed to create batch', 'error');
        },
    });

    const handleAddItem = (item, quantity) => {
        if (quantity <= 0 || quantity > item.remaining_quantity) {
            addToast('Invalid quantity', 'error');
            return;
        }

        setSelectedItems(prev => {
            const newItems = [...prev];
            const existingIndex = newItems.findIndex(i => i.item_id === item.item_id && i.donation_id === item.donation_id);

            if (existingIndex >= 0) {
                newItems[existingIndex].batchQuantity = quantity;
            } else {
                newItems.push({ ...item, batchQuantity: quantity });
            }
            return newItems;
        });
    };

    const handleRemoveItem = (index) => {
        setSelectedItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleCreateBatch = () => {
        if (selectedItems.length === 0) {
            addToast('Please add items to the batch', 'warning');
            return;
        }
        if (!assignedStaff) {
            addToast('Please assign a batch staff member', 'warning');
            return;
        }

        const payload = {
            items: selectedItems.map(i => ({
                donation_id: i.donation_id,
                item_id: i.item_id,
                item_name: i.item_name,
                quantity: parseInt(i.batchQuantity)
            })),
            assigned_to: assignedStaff,
            notes
        };

        createBatchMutation.mutate(payload);
    };

    if (loadingItems) {
        return (
            <div className="flex justify-center items-center h-screen bg-zinc-50 dark:bg-zinc-950">
                <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-10 bg-zinc-50 dark:bg-black min-h-screen text-zinc-900 dark:text-zinc-100 pb-24 lg:pb-8">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center py-6 border-b border-zinc-200 dark:border-zinc-800">
                    <div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-zinc-900 dark:text-white">
                            Batch Operations
                        </h1>
                        <p className="text-zinc-500 dark:text-zinc-400 mt-1 text-lg">
                            Orchestrate logistics and manage fleet assignments.
                        </p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 mt-8">

                    {/* Inventory List */}
                    <div className="lg:col-span-7 space-y-6">
                        <div className="flex items-center justify-between bg-white dark:bg-zinc-900 p-4 rounded-xl shadow-sm border border-zinc-200 dark:border-zinc-800">
                            <h2 className="text-lg font-bold flex items-center gap-2 text-zinc-800 dark:text-zinc-200">
                                <Box className="w-5 h-5 text-blue-500" />
                                Warehouse Inventory
                            </h2>
                            <Badge variant="secondary" className="bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                                {availableItems?.length || 0} SKUs Found
                            </Badge>
                        </div>

                        <div className="grid gap-4">
                            {availableItems && availableItems.length > 0 ? (
                                availableItems.map((item) => {
                                    const isSelected = selectedItems.find(i => i.item_id === item.item_id && i.donation_id === item.donation_id);
                                    const selectedQty = isSelected ? (parseInt(isSelected.batchQuantity) || 0) : 0;
                                    const availableToSelect = item.remaining_quantity - selectedQty;

                                    return (
                                        <div
                                            key={`${item.donation_id}-${item.item_id}`}
                                            className={`group p-5 rounded-xl border transition-all duration-300
                                                        ${isSelected
                                                    ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 shadow-md ring-1 ring-blue-500/20'
                                                    : 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:shadow-lg'
                                                }
                                                    `}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-bold text-lg text-zinc-900 dark:text-white">{item.item_name}</h3>
                                                        {(item.category || item.type) && (
                                                            <Badge variant="outline" className="text-[10px] h-5 border-zinc-300 dark:border-zinc-700 text-zinc-500">
                                                                {item.category || item.type}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1 text-sm text-zinc-500">
                                                        <User className="w-3 h-3" />
                                                        <span>Donor: {item.donor_name}</span>
                                                    </div>
                                                </div>
                                                <Badge className={`${availableToSelect === 0 ? 'bg-zinc-100 text-zinc-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'}`}>
                                                    {availableToSelect} In Stock
                                                </Badge>
                                            </div>

                                            <div className="mt-4 pt-4 border-t border-zinc-100 dark:border-zinc-800/50 flex items-stretch gap-3">
                                                <Input
                                                    type="number"
                                                    placeholder="0"
                                                    id={`qty-${item.item_id}`}
                                                    min="1"
                                                    max={availableToSelect}
                                                    disabled={availableToSelect === 0}
                                                    className="w-20 bg-zinc-50 dark:bg-zinc-950 border-zinc-200 dark:border-zinc-700 text-center"
                                                />
                                                <Button
                                                    size="default"
                                                    disabled={availableToSelect === 0}
                                                    className="flex-1 bg-zinc-900 dark:bg-zinc-100 text-zinc-50 dark:text-zinc-900 hover:bg-zinc-800"
                                                    onClick={() => {
                                                        const qtyInput = document.getElementById(`qty-${item.item_id}`);
                                                        const val = parseInt(qtyInput.value);
                                                        if (val) {
                                                            if (val > availableToSelect) {
                                                                addToast(`Only ${availableToSelect} items available`, 'warning');
                                                                return;
                                                            }
                                                            handleAddItem(item, selectedQty + val);
                                                            qtyInput.value = '';
                                                        }
                                                    }}
                                                >
                                                    <span className="whitespace-nowrap">Add to Batch</span> <ArrowRight className="w-4 h-4 ml-2" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 bg-zinc-50 dark:bg-zinc-900/50 rounded-xl border border-dashed border-zinc-200 dark:border-zinc-800">
                                    <Package className="w-12 h-12 text-zinc-300 mb-2" />
                                    <p className="text-zinc-500">Inventory Empty</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Configuration Panel */}
                    <div className="hidden lg:block lg:col-span-5 relative">
                        <div className="sticky top-8">
                            <BatchConfigCard
                                selectedItems={selectedItems}
                                handleRemoveItem={handleRemoveItem}
                                assignedStaff={assignedStaff}
                                setAssignedStaff={setAssignedStaff}
                                staffList={staffList}
                                notes={notes}
                                setNotes={setNotes}
                                createBatchMutation={createBatchMutation}
                                handleSubmit={handleCreateBatch}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Actions Overlay */}
            <div className="lg:hidden fixed bottom-6 left-6 right-6 z-40 pointer-events-none">
                {selectedItems.length > 0 && (
                    <div className="bg-zinc-900/90 dark:bg-white/90 backdrop-blur-md text-white dark:text-black p-4 rounded-2xl shadow-2xl flex justify-between items-center pointer-events-auto animate-in slide-in-from-bottom-4 border border-white/10 dark:border-zinc-200">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">Manifest</p>
                            <p className="font-bold text-sm flex items-center gap-1.5 mt-0.5">
                                <Box className="w-4 h-4 text-blue-400 dark:text-blue-600" />
                                {selectedItems.length} {selectedItems.length === 1 ? 'Item' : 'Items'} Selected
                            </p>
                        </div>
                        <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 active:scale-95 transition-transform"
                            onClick={() => setIsMobileDrawerOpen(true)}
                        >
                            Configure Batch
                        </Button>
                    </div>
                )}
            </div>

            {/* Mobile Configuration Dialog */}
            <Dialog open={isMobileDrawerOpen} onOpenChange={setIsMobileDrawerOpen}>
                <DialogContent className="max-w-md w-[92vw] bg-white dark:bg-zinc-950 p-6 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-2xl flex flex-col max-h-[85vh]">
                    <DialogHeader className="border-b border-zinc-100 dark:border-zinc-800/50 pb-4">
                        <DialogTitle className="text-xl font-bold flex items-center gap-2">
                            <Box className="w-5 h-5 text-blue-500" />
                            Batch Config
                        </DialogTitle>
                        <DialogDescription className="text-zinc-500 text-xs">
                            Review batch manifest and assign staff personnel.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="overflow-y-auto flex-1 py-4 pr-1">
                        <BatchConfigCard
                            selectedItems={selectedItems}
                            handleRemoveItem={handleRemoveItem}
                            assignedStaff={assignedStaff}
                            setAssignedStaff={setAssignedStaff}
                            staffList={staffList}
                            notes={notes}
                            setNotes={setNotes}
                            createBatchMutation={createBatchMutation}
                            handleSubmit={handleCreateBatch}
                            isMobile={true}
                            setIsMobileDrawerOpen={setIsMobileDrawerOpen}
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Extracted Component
function BatchConfigCard({ selectedItems, handleRemoveItem, assignedStaff, setAssignedStaff, staffList, notes, setNotes, createBatchMutation, handleSubmit, isMobile, setIsMobileDrawerOpen }) {
    return (
        <Card className={`bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 shadow-xl shadow-zinc-200/50 dark:shadow-black/50 ${isMobile ? 'shadow-none border-0' : ''}`}>
            {!isMobile && (
                <div className="p-6 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50 flex items-center justify-between">
                    <div>
                        <h3 className="font-bold text-lg">Batch Manifest</h3>
                        <p className="text-xs text-zinc-500">Finalize logistics details</p>
                    </div>
                    <Badge>{selectedItems.length}</Badge>
                </div>
            )}
            <CardContent className={`${isMobile ? 'p-0' : 'p-6'} space-y-6`}>

                {/* Items List */}
                <div className="space-y-3">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Selected Cargo</Label>
                    <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                        {selectedItems.length > 0 ? (
                            selectedItems.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center p-3 bg-zinc-50 dark:bg-zinc-800/40 rounded-lg border border-zinc-100 dark:border-zinc-800/50">
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-lg bg-white dark:bg-zinc-800 flex items-center justify-center font-bold text-xs border border-zinc-200 dark:border-zinc-700 shadow-sm">
                                            {item.batchQuantity}
                                        </div>
                                        <div className="overflow-hidden">
                                            <p className="font-medium text-sm truncate w-24 md:w-32">{item.item_name}</p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-zinc-400 hover:text-red-500 hover:bg-red-50"
                                        onClick={() => handleRemoveItem(idx)}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 border-2 border-dashed border-zinc-100 dark:border-zinc-800 rounded-xl">
                                <p className="text-xs text-zinc-400">List is empty</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-zinc-200 dark:via-zinc-800 to-transparent" />

                {/* Assignment Controls */}
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-zinc-700 dark:text-zinc-300">Assign Driver / Staff</Label>
                        <Select value={assignedStaff} onValueChange={setAssignedStaff}>
                            <SelectTrigger className="bg-zinc-50 dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800">
                                <SelectValue placeholder="Select Personnel" />
                            </SelectTrigger>
                            <SelectContent>
                                {staffList && staffList.map(staff => (
                                    <SelectItem key={staff._id} value={staff._id}>
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                            {staff.name}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label className="text-zinc-700 dark:text-zinc-300">Notes</Label>
                        <Input
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Gate codes, handling instructions..."
                            className="bg-zinc-50 dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800"
                        />
                    </div>
                </div>

                <Button
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-900/20"
                    onClick={() => {
                        handleSubmit();
                        if (isMobile && setIsMobileDrawerOpen) setIsMobileDrawerOpen(false);
                    }}
                    disabled={createBatchMutation.isPending || selectedItems.length === 0}
                >
                    {createBatchMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : "Confirm & Create Batch"}
                </Button>

            </CardContent>
        </Card>
    );
}
